import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Menu, X } from "lucide-react";
import Monogram from "./Monogram";
import LangSwitcher from "./LangSwitcher";
import MagneticButton from "./MagneticButton";
import { useI18n } from "../i18n/I18nProvider";

export default function Nav() {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { scrollYProgress } = useScroll();
  const scaleX = useTransform(scrollYProgress, [0, 1], [0, 1]);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  const links = [
    { to: "/services", label: t.nav.services },
    { to: "/portfolio", label: t.nav.portfolio },
    { to: "/studio", label: t.nav.about },
    { to: "/pricing", label: t.nav.pricing },
    { to: "/journal", label: t.nav.blog },
    { to: "/contact", label: t.nav.contact },
  ];

  return (
    <>
      <motion.div
        style={{ scaleX }}
        className="fixed top-0 left-0 right-0 h-[2px] bg-rouge origin-left z-[70]"
        aria-hidden
      />
      <header
        className={`fixed top-0 left-0 right-0 z-[65] transition-all duration-500 ${
          scrolled ? "py-3 bg-black/70 backdrop-blur-xl border-b border-white/5" : "py-6 bg-transparent"
        }`}
        data-testid="site-header"
      >
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10 flex items-center justify-between gap-6">
          <div className="text-ink-100"><Monogram size={36} withWord /></div>

          <nav className="hidden lg:flex items-center gap-8 text-[12px] tracking-[0.2em] uppercase text-ink-300">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-cursor={l.label}
                data-testid={`nav-${l.to.replace("/", "")}`}
                className="hover:text-ink-100 transition-colors"
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-5">
            <a
              href="tel:+15068716122"
              aria-label="Call Phaneroo Prod"
              className="hidden xl:flex items-center gap-1.5 text-ink-300 hover:text-ink-100 text-[11px] tracking-[0.15em] transition-colors"
            >
              <svg xmlns='http://www.w3.org/2000/svg' width='13' height='13' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'><path d='M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.63 3.4 2 2 0 0 1 3.6 1.21h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 9a16 16 0 0 0 6.06 6.06l1.27-.93a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z'/></svg>
              +1 (506) 871‑6122
            </a>
            <LangSwitcher className="hidden sm:flex" />
            <MagneticButton
              as={Link}
              to="/contact"
              data-testid="cta-quote"
              data-cursor={t.nav.quote}
              className="hidden sm:inline-flex items-center bg-rouge hover:bg-rouge-deep text-ink-100 px-5 py-3 text-[11px] tracking-[0.22em] uppercase transition-colors"
            >
              {t.nav.quote}
            </MagneticButton>
            <button
              aria-label="menu"
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((s) => !s)}
              className="lg:hidden text-ink-100 p-2"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </header>

      <motion.div
        initial={false}
        animate={open ? { opacity: 1, pointerEvents: "auto" } : { opacity: 0, pointerEvents: "none" }}
        transition={{ duration: 0.3 }}
        className="fixed inset-0 z-[64] bg-black/95 backdrop-blur-xl lg:hidden flex flex-col pt-28 px-8"
      >
        <nav className="flex flex-col gap-6">
          {links.map((l, i) => (
            <motion.div
              key={l.to}
              initial={{ opacity: 0, x: -20 }}
              animate={open ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
              transition={{ delay: open ? 0.05 * i : 0, duration: 0.4 }}
            >
              <Link
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.to.replace("/", "")}`}
                className="font-serif-editorial text-5xl text-ink-100"
              >
                {l.label}
              </Link>
            </motion.div>
          ))}
        </nav>
        <div className="mt-12 flex items-center gap-6">
          <LangSwitcher />
          <Link
            to="/contact"
            onClick={() => setOpen(false)}
            className="bg-rouge text-ink-100 px-5 py-3 text-[11px] tracking-[0.22em] uppercase"
            data-testid="mobile-cta-quote"
          >
            {t.nav.quote}
          </Link>
        </div>
      </motion.div>
    </>
  );
}
